'use client';

import * as React from 'react';
import Link from 'next/link';
import { useMDXComponent } from '@content-collections/mdx/react';
import { ArrowUpRight } from 'lucide-react';

import { cn } from '@workspace/ui/lib/utils';

type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement>;
type AeoPageType = 'guide' | 'product' | 'use-case' | 'comparison' | 'hub';

function getTextContent(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(getTextContent).join('');
  }

  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return getTextContent(node.props.children);
  }

  return '';
}

function AeoLink({ href = '', children, className, ...props }: AnchorProps) {
  const styles = cn(
    'font-medium text-emerald-700 underline decoration-emerald-600/30 underline-offset-4 transition-colors hover:text-emerald-800 hover:decoration-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300',
    className
  );

  if (href.startsWith('/')) {
    return (
      <Link
        href={href}
        className={styles}
      >
        {children}
      </Link>
    );
  }

  const isExternal = href.startsWith('http');

  return (
    <a
      href={href}
      className={styles}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noreferrer' : undefined}
      {...props}
    >
      {children}
      {isExternal && (
        <ArrowUpRight
          aria-hidden="true"
          className="ml-1 inline size-3.5 align-baseline"
        />
      )}
    </a>
  );
}

function AeoH1({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h1
      className={cn(
        'display-h1 max-w-4xl scroll-m-24 text-balance text-4xl text-[var(--ink)] sm:text-5xl lg:text-6xl',
        className
      )}
      {...props}
    />
  );
}

function AeoH2({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  const isQuestion = getTextContent(children).trim().endsWith('?');

  return (
    <h2
      className={cn(
        'display-h2 mt-16 scroll-m-24 border-t border-[var(--hairline)] pt-10 text-balance text-3xl text-[var(--ink)] sm:text-4xl',
        isQuestion && 'aeo-question-heading',
        className
      )}
      {...props}
    >
      {children}
    </h2>
  );
}

function AeoH3({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  const text = getTextContent(children).trim();

  return (
    <h3
      className={cn(
        'mt-10 scroll-m-24 text-balance text-xl font-semibold tracking-[-0.02em] text-[var(--ink)] sm:text-2xl',
        text.endsWith('?') && 'aeo-question-heading',
        /^\d+\./.test(text) && 'aeo-step-heading',
        className
      )}
      {...props}
    >
      {children}
    </h3>
  );
}

function AeoParagraph({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  const isNextStep = getTextContent(children).trim().startsWith('Next step:');

  return (
    <p
      className={cn(
        'mt-5 text-pretty text-base leading-7 text-[var(--ink-3)] sm:text-[17px] sm:leading-8',
        isNextStep && 'aeo-next-step',
        className
      )}
      {...props}
    >
      {children}
    </p>
  );
}

function createComponents(comparisonTarget?: string) {
  const comparisonStyle = comparisonTarget
    ? ({
        '--comparison-target': `"${comparisonTarget.replace(/"/g, '\\"')}"`
      } as React.CSSProperties)
    : undefined;

  return {
    h1: AeoH1,
    h2: AeoH2,
    h3: AeoH3,
    h4: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
      <h4
        className={cn(
          'mt-8 scroll-m-24 text-lg font-semibold tracking-tight text-[var(--ink)]',
          className
        )}
        {...props}
      />
    ),
    p: AeoParagraph,
    a: AeoLink,
    strong: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
      <strong
        className={cn('font-semibold text-[var(--ink-2)]', className)}
        {...props}
      />
    ),
    ul: ({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
      <ul
        className={cn(
          'my-6 ml-6 list-disc space-y-3 text-[var(--ink-3)] marker:text-emerald-600',
          className
        )}
        {...props}
      />
    ),
    ol: ({ className, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
      <ol
        className={cn(
          'my-6 ml-6 list-decimal space-y-3 text-[var(--ink-3)] marker:font-mono marker:text-xs marker:font-semibold marker:text-emerald-700 dark:marker:text-emerald-400',
          className
        )}
        {...props}
      />
    ),
    li: ({ className, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
      <li
        className={cn('leading-7 sm:leading-8', className)}
        {...props}
      />
    ),
    blockquote: ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLElement>) => (
      <blockquote
        className={cn(
          'my-8 border-l-2 border-emerald-500 bg-[var(--mint)] px-6 py-1 text-[var(--ink-2)] [&>p]:mt-4 [&>p:last-child]:mb-4',
          className
        )}
        {...props}
      />
    ),
    hr: ({ className, ...props }: React.HTMLAttributes<HTMLHRElement>) => (
      <hr
        className={cn('my-12 border-[var(--hairline)]', className)}
        {...props}
      />
    ),
    table: ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLTableElement>) => (
      <div
        className="aeo-table-frame my-8 w-full overflow-x-auto border-y border-[var(--hairline)]"
        style={comparisonStyle}
      >
        <table
          className={cn(
            'w-full min-w-[680px] border-collapse text-left',
            className
          )}
          {...props}
        />
      </div>
    ),
    thead: ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLTableSectionElement>) => (
      <thead
        className={cn('bg-[var(--paper-2)]', className)}
        {...props}
      />
    ),
    tr: ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLTableRowElement>) => (
      <tr
        className={cn(
          'border-b border-[var(--hairline-2)] last:border-0',
          className
        )}
        {...props}
      />
    ),
    th: ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLTableCellElement>) => (
      <th
        scope="col"
        className={cn(
          'px-4 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--ink-3)]',
          className
        )}
        {...props}
      />
    ),
    td: ({
      className,
      ...props
    }: React.HTMLAttributes<HTMLTableCellElement>) => (
      <td
        className={cn(
          'px-4 py-4 align-top text-sm leading-6 text-[var(--ink-3)] sm:text-[15px]',
          className
        )}
        {...props}
      />
    )
  };
}

type AeoMdxProps = {
  code: string;
  pageType?: AeoPageType;
  comparisonTarget?: string;
};

export function AeoMdx({
  code,
  pageType,
  comparisonTarget
}: AeoMdxProps): React.JSX.Element {
  const Component = useMDXComponent(code);
  const components = createComponents(comparisonTarget);

  return (
    <div
      className={cn(
        'aeo-prose min-w-0',
        pageType !== 'comparison' && 'aeo-editorial',
        pageType === 'comparison' && 'aeo-comparison'
      )}
    >
      <Component components={components} />
    </div>
  );
}
